// Minimal BibTeX → APA converter.
//
// The upload panel has always advertised `.bib` in its file picker, but the file
// was read as plain text and fed to an APA prose parser, which produced garbage.
// This converts entries to APA lines that the existing parser understands.
//
// ponytail: deliberately a small field-scraper, not a real BibTeX grammar — no
// @string macros, no cross-refs, no BibTeX-escaped accents. Swap in a proper
// parser (citation-js) only if users bring files this chokes on.

import { buildApa } from './apa.js'

const TYPE_MAP = {
  article:       'JOURNAL',
  book:          'BOOK',
  inbook:        'BOOK',
  incollection:  'BOOK',
  booklet:       'BOOK',
  techreport:    'REPORT',
  manual:        'REPORT',
  phdthesis:     'REPORT',
  mastersthesis: 'REPORT',
  online:        'WEBSITE',
  electronic:    'WEBSITE',
  misc:          'OTHER',
}

// Strips the wrapping braces/quotes and the common TeX noise.
function cleanValue(v) {
  return String(v || '')
    .replace(/^[{"]+|[}"]+$/g, '')
    .replace(/[{}]/g, '')
    .replace(/\\[a-zA-Z]+\s*/g, '')
    .replace(/~/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// BibTeX authors are "Last, First and Last, First" — APA wants ", & " last.
function formatAuthors(raw) {
  const parts = cleanValue(raw).split(/\s+and\s+/i).filter(Boolean)
  if (!parts.length) return ''

  const names = parts.map(p => {
    if (p.includes(',')) {
      const [last, first = ''] = p.split(',')
      const initials = first.trim().split(/\s+/).filter(Boolean)
        .map(n => `${n[0].toUpperCase()}.`).join(' ')
      return initials ? `${last.trim()}, ${initials}` : last.trim()
    }
    // "First Last" form
    const bits = p.trim().split(/\s+/)
    const last = bits.pop()
    const initials = bits.map(n => `${n[0].toUpperCase()}.`).join(' ')
    return initials ? `${last}, ${initials}` : last
  })

  if (names.length === 1) return names[0]
  return `${names.slice(0, -1).join(', ')}, & ${names[names.length - 1]}`
}

// Pulls the fields out of one @type{...} entry body.
function parseFields(body) {
  const fields = {}
  // key = {value} | "value" | bareword, brace-balanced for the {…} case.
  const re = /(\w+)\s*=\s*(\{(?:[^{}]|\{[^{}]*\})*\}|"[^"]*"|[^,\s}]+)/g
  let m
  while ((m = re.exec(body)) !== null) fields[m[1].toLowerCase()] = cleanValue(m[2])
  return fields
}

export function parseBibtex(input) {
  const entries = []
  const text = String(input || '')

  // Walk @type{ … } with brace counting so nested braces don't end an entry early.
  const head = /@(\w+)\s*\{/g
  let m
  while ((m = head.exec(text)) !== null) {
    const type = m[1].toLowerCase()
    if (type === 'comment' || type === 'preamble' || type === 'string') continue

    let depth = 1
    let i = head.lastIndex
    while (i < text.length && depth > 0) {
      if (text[i] === '{') depth++
      else if (text[i] === '}') depth--
      i++
    }

    const fields = parseFields(text.slice(head.lastIndex, i - 1))
    entries.push({
      authors: formatAuthors(fields.author || fields.editor),
      year:    (fields.year || '').match(/\d{4}/)?.[0] || '',
      title:   fields.title || '',
      journal: fields.journal || fields.booktitle || fields.publisher || fields.institution || fields.school || '',
      volume:  fields.volume || '',
      issue:   fields.number || fields.issue || '',
      pages:   (fields.pages || '').replace(/--/g, '–'),
      doi:     fields.doi || '',
      url:     fields.url || '',
      type:    TYPE_MAP[type] || 'OTHER',
    })

    head.lastIndex = i
  }

  return entries.filter(e => e.title || e.authors)
}

// One APA reference per blank-line-separated block, which is exactly what the
// server-side parseAPA expects.
export function bibtexToApa(input) {
  return parseBibtex(input).map(buildApa).join('\n\n')
}

export default bibtexToApa
